import { useState } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  alpha,
  TextField,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';

const LinkForm = ({
  app,
  deployment,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
}) => {
  const [links, setLinks] = useState({
    backgroundImageLink: deployment?.config?.backgroundImageLink || '',
    logoLink: deployment?.config?.logoLink || '',
    impressumLink: deployment?.config?.impressumLink || '',
    privacyLink: deployment?.config?.privacyLink || '',
  });

  const [errors, setErrors] = useState({});
  const [defaultExpiryValue, setDefaultExpiryValue] = useState(
    deployment?.config?.defaultExpiryValue?.toString() || '1',
  );
  const [defaultExpiryUnit, setDefaultExpiryUnit] = useState(
    deployment?.config?.defaultExpiryUnit || 'Tage',
  );

  const handleLinkChange = (field) => (event) => {
    const value = event.target.value;
    setLinks((prev) => ({ ...prev, [field]: value }));

    // Validate URL if value is not empty
    if (value && !isValidUrl(value)) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Bitte geben Sie eine gültige URL ein',
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    // Filter out empty links
    const configuredLinks = Object.entries(links).reduce(
      (acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      },
      {},
    );

    next({
      ...configuredLinks,
      defaultExpiryValue,
      defaultExpiryUnit,
    });
  };

  const linkFields = [
    { id: 'backgroundImageLink', label: 'Hintergrundbild URL' },
    { id: 'logoLink', label: 'Logo URL' },
    { id: 'impressumLink', label: 'Impressum URL' },
    { id: 'privacyLink', label: 'Datenschutz URL' },
  ];

  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<LinkIcon color="primary" />}
        title={<Typography variant="h6">Link-Konfiguration</Typography>}
        subheader={
          <Typography variant="subtitle2" color="text.secondary">
            Für <b>{app?.title || deployment?.title}</b>
          </Typography>
        }
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.08),
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1,
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Geben Sie optional die benötigten URLs ein, um Ihr Deployment zu
          personalisieren.
          <br />
          <span style={{ color: '#888', fontSize: '0.95em' }}>
            Leere Felder werden ignoriert.
          </span>
        </Typography>

        <Stack spacing={2}>
          {linkFields.map(({ id, label }) => (
            <TextField
              key={id}
              fullWidth
              label={label}
              value={links[id]}
              onChange={handleLinkChange(id)}
              error={!!errors[id]}
              helperText={errors[id] || ''}
              placeholder="https://"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          ))}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
            <TextField
              label="Ablaufdauer"
              type="number"
              value={defaultExpiryValue}
              onChange={(e) => setDefaultExpiryValue(e.target.value.toString())}
              sx={{ width: 120 }}
              inputProps={{ min: 1 }}
              variant="outlined"
            />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="expiry-unit-label">Einheit</InputLabel>
              <Select
                labelId="expiry-unit-label"
                value={defaultExpiryUnit}
                label="Einheit"
                onChange={(e) => setDefaultExpiryUnit(e.target.value)}
              >
                <MenuItem value="Tage">Tage</MenuItem>
                <MenuItem value="Wochen">Wochen</MenuItem>
                <MenuItem value="Stunden">Stunden</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Gültigkeit für generierte Links
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={Object.keys(errors).length > 0}
        />
      </CardActions>
    </Card>
  );
};

export default LinkForm;
